import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  PhotoIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { classNames, sleep } from "../../../utils";
import * as z from "zod";
import { useRef, useState } from "react";
import { PersonFieldsType, SavePersonInput } from "../../../api/types";

const schema = z.object({
  firstname: z
    .string()
    .min(1, { message: "Champs requis" })
    .max(50, { message: "50 caractères maximum" }),
  lastname: z
    .string()
    .min(1, { message: "Champs requis" })
    .max(50, { message: "50 caractères maximum" }),
  number: z
    .string()
    .regex(/^\d{1,3}$/, { message: "1 à 3 chiffres requis" })
    .optional(),
  // licence_no: z.string().regex(/^\d{13}$/, { message: "13 chiffres requis" }),
  role: z.enum(["manager", "coach", "physio", "assistant", "player"]),
});

interface AddPersonFormDrawerProps {
  role: "player" | "staff";
  teamId: string;
  initialData?: PersonFieldsType;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSavePerson: (person: SavePersonInput) => Promise<void>;
}

export default function AddPersonFormDrawer({
  role,
  teamId,
  initialData,
  open,
  setOpen,
  onSavePerson,
}: AddPersonFormDrawerProps) {
  const imageRef = useRef<HTMLInputElement>(null);
  const autorisationRef = useRef<HTMLInputElement>(null);
  const licenceRef = useRef<HTMLInputElement>(null);

  const [imageURI, setImageURI] = useState<File | null>(null);
  const [autorisationURI, setAutorisationURI] = useState<File | null>(null);
  const [licenceURI, setLicenceURI] = useState<File | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: initialData,
  });

  const imageURIData = imageURI ? URL.createObjectURL(imageURI) : null;

  const isPlayer = role === "player";

  const onCancel = () => {
    // Reset file inputs states
    setImageURI(null);
    setAutorisationURI(null);
    setLicenceURI(null);
    // Reset file inputs values
    if (imageRef?.current) {
      imageRef.current.value = "";
    }
    if (autorisationRef?.current) {
      autorisationRef.current.value = "";
    }
    if (licenceRef?.current) {
      licenceRef.current.value = "";
    }

    // Reset form values
    reset();
    // Close the drawer
    setOpen(false);
  };

  const onSave = async (data: FieldValues) => {
    const imageList = imageRef.current?.files
      ? Array.from(imageRef.current.files)
      : [];
    const autorisationList = autorisationRef.current?.files
      ? Array.from(autorisationRef.current.files)
      : [];
    const licenceList = licenceRef.current?.files
      ? Array.from(licenceRef.current.files)
      : [];
    const image = imageList?.[0];
    const autorisation = autorisationList?.[0];
    const licence = licenceList?.[0];

    setIsAdding(true);
    try {
      await onSavePerson({
        firstname: data.firstname,
        lastname: data.lastname,
        number: Number(data.number),
        status: "inscrit",
        team_register: [teamId],
        role: data.role,
        ...(image?.size > 0 ? { image } : {}),
        ...(autorisation?.size > 0 ? { autorisation } : {}),
        ...(licence?.size > 0 ? { licence } : {}),
      });
      sleep(2000);
      reset();
      setOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsAdding(false);
    }
  };

  const onImageClick = () => {
    imageRef.current?.click();
  };

  const onFileInsert = (
    e: React.ChangeEvent<HTMLInputElement>,
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (file: File) => void
  ) => {
    const fileList = e.target.files;
    if (fileList) {
      // 1. covert to File[] using Array.from
      const files: File[] = Array.from(fileList);
      // 2. now you can use array methods on files;
      const file = files?.[0];
      if (!file) {
        return;
      }
      // 3. now let's create a DataTransfer to get a FileList
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      // @ts-expect-error
      inputRef.files = dataTransfer.files;
      // 4. call the onChange function
      onChange(file);
    }
  };

  const onFileRemove = (
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (file: File | null) => void
  ) => {
    if (inputRef.current) {
      inputRef.current.value = "";
      onChange(null);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} className="relative z-10">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <form
                onSubmit={handleSubmit(onSave)}
                className="flex h-full flex-col divide-y divide-gray-200 bg-gray-100 shadow-xl"
              >
                <div className="h-0 flex-1 overflow-y-auto">
                  <div className="bg-indigo-700 px-4 py-6 sm:px-6">
                    <div className="flex items-center justify-between">
                      <DialogTitle className="text-base font-semibold text-white">
                        Nouveau {role === "player" ? "joueur" : "staff"}
                      </DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1">
                      <p className="text-sm text-indigo-300">
                        Ajouter un nouveau membre à l'équipe
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col justify-between">
                    <div className="divide-y divide-gray-200 px-4 sm:px-6">
                      <div className="space-y-6 pb-5 pt-6">
                        <div className="col-span-full">
                          <label
                            htmlFor="photo"
                            className="block text-sm/6 font-medium text-gray-900"
                          >
                            Photo
                          </label>
                          <div className="mt-2 flex items-center gap-x-3">
                            {imageURIData ? (
                              <img
                                src={imageURIData}
                                alt="membre"
                                className="w-12 h-12 rounded-full"
                              />
                            ) : (
                              <UserCircleIcon
                                aria-hidden="true"
                                className="w-12 text-gray-300"
                              />
                            )}
                            <input
                              {...register("image")}
                              onChange={(e) =>
                                onFileInsert(e, imageRef, setImageURI)
                              }
                              ref={imageRef}
                              id="photo"
                              type="file"
                              hidden
                            />
                            <button
                              type="button"
                              className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              onClick={onImageClick}
                            >
                              Changer
                            </button>
                          </div>
                        </div>
                        {!isPlayer ? (
                          <div className="sm:col-span-3">
                            <label
                              htmlFor="role"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              Rôle
                            </label>
                            <div className="mt-2 grid grid-cols-1">
                              <select
                                {...register("role")}
                                id="role"
                                name="role"
                                className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                              >
                                <option value="manager">Manager</option>
                                <option value="coach">Coach</option>
                                <option value="physio">Kiné</option>
                                <option value="assistant">
                                  Adjoint Terrain
                                </option>
                              </select>
                              <ChevronDownIcon
                                aria-hidden="true"
                                className="pointer-events-none col-start-1 row-start-1 mr-2 w-5 h-5 self-center justify-self-end text-gray-500 sm:w-4 sm:h-4"
                              />
                            </div>
                          </div>
                        ) : (
                          <input
                            {...register("role")}
                            type="hidden"
                            value="player"
                          />
                        )}
                        <div>
                          <label
                            htmlFor="firstname"
                            className="block text-sm/6 font-medium text-gray-900"
                          >
                            Prénom*
                          </label>
                          <div className="mt-2 grid grid-cols-1">
                            <input
                              {...register("firstname")}
                              id="firstname"
                              type="text"
                              autoComplete="given-name"
                              placeholder="Jean"
                              minLength={1}
                              maxLength={50}
                              className={classNames(
                                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                                errors.firstname
                                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
                              )}
                            />
                            {errors.firstname && (
                              <ExclamationCircleIcon
                                aria-hidden="true"
                                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
                              />
                            )}
                          </div>
                          {errors?.firstname?.message && (
                            <p
                              id="firstname-error"
                              className="mt-2 text-sm text-red-600"
                            >
                              {errors.firstname.message.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="lastname"
                            className="block text-sm/6 font-medium text-gray-900"
                          >
                            Nom*
                          </label>
                          <div className="mt-2 grid grid-cols-1">
                            <input
                              {...register("lastname")}
                              id="lastname"
                              type="text"
                              autoComplete="family-name"
                              placeholder="Dupont"
                              minLength={1}
                              maxLength={50}
                              className={classNames(
                                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                                errors.lastname
                                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
                              )}
                            />
                            {errors.lastname && (
                              <ExclamationCircleIcon
                                aria-hidden="true"
                                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
                              />
                            )}
                          </div>
                          {errors?.lastname?.message && (
                            <p
                              id="lastname-error"
                              className="mt-2 text-sm text-red-600"
                            >
                              {errors.lastname.message.toString()}
                            </p>
                          )}
                        </div>
                        {isPlayer && (
                          <div className="sm:col-span-3">
                            <label
                              htmlFor="number"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              Numéro Maillot*
                            </label>
                            <div className="mt-2 grid grid-cols-1">
                              <input
                                {...register("number")}
                                id="number"
                                type="text"
                                placeholder="7"
                                minLength={1}
                                maxLength={3}
                                className={classNames(
                                  "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                                  errors.number
                                    ? "outline-red-300 placeholder-red-300 focus:outline-red-600"
                                    : "outline-gray-300 placeholder-gray-400 focus:outline-indigo-600"
                                )}
                              />
                              {errors.number && (
                                <ExclamationCircleIcon
                                  aria-hidden="true"
                                  className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
                                />
                              )}
                            </div>
                            {errors?.number?.message && (
                              <p
                                id="number-error"
                                className="mt-2 text-sm text-red-600"
                              >
                                {errors.number.message.toString()}
                              </p>
                            )}
                          </div>
                        )}
                        {/* <div className="sm:col-span-3">
                          <label
                            htmlFor="licence_no"
                            className="block text-sm/6 font-medium text-gray-900"
                          >
                            Numéro de Licence FFR
                          </label>
                          <div className="mt-2 grid grid-cols-1">
                            <input
                              {...register("licence_no")}
                              id="licence_no"
                              type="text"
                              placeholder="1234567890123"
                              minLength={13}
                              maxLength={13}
                              className={classNames(
                                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                                errors.licence_no
                                  ? "outline-red-300 placeholder-red-300 focus:outline-red-600"
                                  : "outline-gray-300 placeholder-gray-400 focus:outline-indigo-600"
                              )}
                            />
                            {errors.licence_no && (
                              <ExclamationCircleIcon
                                aria-hidden="true"
                                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
                              />
                            )}
                          </div>
                          {errors?.licence_no?.message && (
                            <p
                              id="licence_no-error"
                              className="mt-2 text-sm text-red-600"
                            >
                              {errors.licence_no.message.toString()}
                            </p>
                          )}
                        </div> */}
                        {isPlayer && (
                          <div className="col-span-full">
                            <label
                              htmlFor="licence"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              {"Licence FFR"}
                            </label>
                            {licenceURI ? (
                              <div className="flex items-center justify-between mt-2 rounded-lg bg-gray-200 py-2 px-2">
                                <div className="flex items-center gap-x-2">
                                  <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm/6 text-blue-500">
                                    {licenceURI?.name.slice(0, 40) +
                                      (licenceURI?.name?.length > 40
                                        ? "..."
                                        : "")}
                                  </span>
                                </div>
                                <TrashIcon
                                  className="w-4 h-4 ml-4 text-blue-500 cursor-pointer"
                                  onClick={() =>
                                    onFileRemove(licenceRef, setLicenceURI)
                                  }
                                />
                              </div>
                            ) : (
                              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                                <div className="text-center">
                                  <PhotoIcon
                                    aria-hidden="true"
                                    className="mx-auto w-12 text-gray-300"
                                  />
                                  <div className="mt-4 flex text-sm/6 text-gray-600">
                                    <label
                                      htmlFor="licence"
                                      className="relative cursor-pointer px-2 rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                    >
                                      <span>Insérer un fichier</span>
                                    </label>
                                    <p className="pl-1">ou glisser-déposer</p>
                                  </div>
                                  <p className="text-xs/5 text-gray-600">
                                    PNG - JPG - PDF jusqu'à 5MB
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              ref={licenceRef}
                              id="licence"
                              type="file"
                              className="sr-only"
                              onChange={(e) =>
                                onFileInsert(e, licenceRef, setLicenceURI)
                              }
                            />
                          </div>
                        )}
                        {isPlayer && (
                          <div className="col-span-full">
                            <label
                              htmlFor="autorisation"
                              className="block text-sm/6 font-medium text-gray-900"
                            >
                              {"Autorisation du président de club*"}
                              <br />
                              <a
                                href="https://api.www.ffr.fr/wp-content/uploads/2022/11/autorisation-president-de-club-rugby-a-7.pdf"
                                target="_blank"
                                className="text-sm text-gray-500"
                                rel="noreferrer"
                              >
                                🔗 Télécharger le modèle
                              </a>
                            </label>
                            {autorisationURI ? (
                              <div className="flex items-center justify-between mt-2 rounded-lg bg-gray-200 py-2 px-2">
                                <div className="flex items-center gap-x-2">
                                  <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm/6 text-blue-500">
                                    {autorisationURI?.name.slice(0, 40) +
                                      (autorisationURI?.name?.length > 40
                                        ? "..."
                                        : "")}
                                  </span>
                                </div>
                                <TrashIcon
                                  className="w-4 h-4 ml-4 text-blue-500 cursor-pointer"
                                  onClick={() =>
                                    onFileRemove(
                                      autorisationRef,
                                      setAutorisationURI
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                                <div className="text-center">
                                  <PhotoIcon
                                    aria-hidden="true"
                                    className="mx-auto w-12 text-gray-300"
                                  />
                                  <div className="mt-4 flex text-sm/6 text-gray-600">
                                    <label
                                      htmlFor="autorisation"
                                      className="relative cursor-pointer px-2 rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                    >
                                      <span>Insérer un fichier</span>
                                    </label>
                                    <p className="pl-1">ou glisser-déposer</p>
                                  </div>
                                  <p className="text-xs/5 text-gray-600">
                                    PNG - JPG - PDF jusqu'à 5MB
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              {...register("autorisation")}
                              ref={autorisationRef}
                              id="autorisation"
                              type="file"
                              className="sr-only"
                              onChange={(e) =>
                                onFileInsert(
                                  e,
                                  autorisationRef,
                                  setAutorisationURI
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 justify-end px-4 py-4">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={!isValid || isAdding}
                    className={classNames(
                      "ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                      !isValid || isAdding
                        ? "cursor-not-allowed opacity-50 disabled"
                        : ""
                    )}
                  >
                    {isAdding ? "Enregistrement..." : "Enregistrer"}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
